import type { InvoiceFormatSettings, SubcontractorBillLineSetting } from "./invoiceFormatSettings";
import type { ConstructionTimelinePhase, MilestoneTemplate } from "../types";
import {
  isTimelineBillLine,
  resolveSubcontractorBillLine,
  syntheticMilestoneBillItemId,
  syntheticTimelineBillItemId,
} from "./subcontractorBillFormatLines";
import { timelineBillLineLabel } from "./invoiceBillLineLabels";

function subLineByItemId(
  subcontractorFallback: SubcontractorBillLineSetting[],
  itemId: string,
): SubcontractorBillLineSetting | undefined {
  return subcontractorFallback.find((row) => row.milestoneTemplateItemId === itemId);
}

function labourPricingFields(
  prev: SubcontractorBillLineSetting | undefined,
  sub: SubcontractorBillLineSetting | undefined,
): Pick<SubcontractorBillLineSetting, "qty" | "unit" | "rate" | "hsnCode"> {
  const rate = (prev?.rate ?? 0) > 0 ? prev!.rate : (sub?.rate ?? 0);
  const qty = (prev?.qty ?? 0) > 0 ? prev!.qty : (sub?.qty ?? 1);
  return {
    qty,
    unit: prev?.unit?.trim() || sub?.unit?.trim() || "LS",
    rate,
    hsnCode: prev?.hsnCode?.trim() || sub?.hsnCode?.trim() || "",
  };
}

export function buildMilestoneLabourBillLinesFromMasters(
  milestoneTemplates: MilestoneTemplate[],
  existing: SubcontractorBillLineSetting[] = [],
  subcontractorFallback: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  const byItemId = new Map(existing.map((row) => [row.milestoneTemplateItemId, row]));
  const out: SubcontractorBillLineSetting[] = [];
  const sortedMilestones = [...milestoneTemplates].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
  );

  for (const tpl of sortedMilestones) {
    const billItems = tpl.items.filter((item) => item.kind === "subcontractor_bill");

    if (billItems.length > 0) {
      for (const item of billItems) {
        const prev = byItemId.get(item.id);
        const sub = subLineByItemId(subcontractorFallback, item.id);
        out.push({
          billSource: "milestone",
          milestoneTemplateId: tpl.id,
          milestoneTemplateItemId: item.id,
          milestoneTitle: tpl.title,
          lineLabel: prev?.lineLabel?.trim() || timelineBillLineLabel(tpl.title, "labour"),
          timelinePhaseIds: [],
          timelinePhaseTitles: [],
          ...labourPricingFields(prev, sub),
        });
      }
      continue;
    }

    const syntheticId = syntheticMilestoneBillItemId(tpl.id);
    const prev = byItemId.get(syntheticId);
    const sub = subLineByItemId(subcontractorFallback, syntheticId);
    out.push({
      billSource: "milestone",
      milestoneTemplateId: tpl.id,
      milestoneTemplateItemId: syntheticId,
      milestoneTitle: tpl.title,
      lineLabel: prev?.lineLabel?.trim() || timelineBillLineLabel(tpl.title, "labour"),
      timelinePhaseIds: [],
      timelinePhaseTitles: [],
      ...labourPricingFields(prev, sub),
    });
  }

  return out;
}

export function buildTimelineLabourBillLinesFromMasters(
  timelinePhases: ConstructionTimelinePhase[],
  existing: SubcontractorBillLineSetting[] = [],
  subcontractorFallback: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  const byItemId = new Map(existing.map((row) => [row.milestoneTemplateItemId, row]));
  const out: SubcontractorBillLineSetting[] = [];
  const sortedPhases = [...timelinePhases].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.title.localeCompare(b.title),
  );

  for (const phase of sortedPhases) {
    const phaseId = String(phase.id);
    const syntheticId = syntheticTimelineBillItemId(phaseId);
    const prev = byItemId.get(syntheticId);
    const sub = subLineByItemId(subcontractorFallback, syntheticId);
    out.push({
      billSource: "timeline",
      milestoneTemplateId: "",
      milestoneTemplateItemId: syntheticId,
      milestoneTitle: phase.title,
      lineLabel: prev?.lineLabel?.trim() || timelineBillLineLabel(phase.title, "labour"),
      timelinePhaseIds: [phaseId],
      timelinePhaseTitles: [phase.title],
      ...labourPricingFields(prev, sub),
    });
  }

  return out;
}

export function buildLabourBillLinesFromMasters(
  milestoneTemplates: MilestoneTemplate[],
  timelinePhases: ConstructionTimelinePhase[],
  existing: SubcontractorBillLineSetting[] = [],
  subcontractorFallback: SubcontractorBillLineSetting[] = [],
): SubcontractorBillLineSetting[] {
  return [
    ...buildMilestoneLabourBillLinesFromMasters(milestoneTemplates, existing, subcontractorFallback),
    ...buildTimelineLabourBillLinesFromMasters(timelinePhases, existing, subcontractorFallback),
  ];
}

/** Copy qty / unit / rate / HSN from matching subcontractor bill lines (keeps labour line labels). */
export function copySubcontractorRatesToLabourLines(
  labourLines: SubcontractorBillLineSetting[],
  subcontractorLines: SubcontractorBillLineSetting[],
): SubcontractorBillLineSetting[] {
  const subById = new Map(subcontractorLines.map((row) => [row.milestoneTemplateItemId, row]));
  return labourLines.map((lab) => {
    const sub = subById.get(lab.milestoneTemplateItemId);
    if (!sub) return lab;
    return {
      ...lab,
      qty: sub.qty > 0 ? sub.qty : lab.qty,
      unit: sub.unit.trim() || lab.unit,
      rate: sub.rate,
      hsnCode: sub.hsnCode.trim() || lab.hsnCode,
    };
  });
}

export function milestoneLabourBillLines(lines: SubcontractorBillLineSetting[]): SubcontractorBillLineSetting[] {
  return lines.filter((row) => !isTimelineBillLine(row));
}

export function timelineLabourBillLines(lines: SubcontractorBillLineSetting[]): SubcontractorBillLineSetting[] {
  return lines.filter(isTimelineBillLine);
}

export function resolveLabourBillLine(
  settings: InvoiceFormatSettings,
  templateItemId: string,
): SubcontractorBillLineSetting | undefined {
  return settings.labourBillLines.find((row) => row.milestoneTemplateItemId === templateItemId);
}

export function applyLabourBillFormatToLine<
  T extends {
    templateItemId: string;
    lineLabel: string;
    lineUnit: string;
    eligibleQty: number;
    rate: number;
    hsnCode: string;
    milestoneTitle?: string;
  },
>(line: T, settings: InvoiceFormatSettings): T {
  const cfg = resolveLabourBillLine(settings, line.templateItemId);
  const subCfg =
    cfg && cfg.rate > 0 ? undefined : resolveSubcontractorBillLine(settings, line.templateItemId);
  const pricing = cfg?.rate > 0 ? cfg : subCfg;
  if (!cfg && !pricing) return line;
  return {
    ...line,
    lineLabel: cfg?.lineLabel.trim() || line.lineLabel,
    lineUnit: (cfg?.unit.trim() || pricing?.unit.trim() || line.lineUnit),
    eligibleQty:
      (cfg?.qty ?? 0) > 0 ? cfg!.qty : (pricing?.qty ?? 0) > 0 ? pricing!.qty : line.eligibleQty,
    rate: (cfg?.rate ?? 0) > 0 ? cfg!.rate : (pricing?.rate ?? 0) > 0 ? pricing!.rate : line.rate,
    hsnCode: cfg?.hsnCode.trim() || pricing?.hsnCode.trim() || line.hsnCode,
    milestoneTitle:
      cfg?.timelinePhaseTitles[0]?.trim() ||
      cfg?.milestoneTitle.trim() ||
      line.milestoneTitle,
  };
}
